/* eslint-disable @typescript-eslint/no-explicit-any */
import { registerGsap } from "./gsap";
import { getLenis } from "./smooth-scroll";

/**
 * Parallax helpers. Layers drift via the CSS variable --py (feeds the independent
 * `translate` property, so it never fights reveal tweens). `k` scales the effect
 * (smaller on phones). Call inside a gsap.context / matchMedia so it is reverted.
 */
export function makeParallax(k: number) {
  const { gsap } = registerGsap();
  const lenis = getLenis();

  function layer(host: string, sel: string, amount: number | number[], mode: "pass" | "settle", start: string, end: string) {
    const hostEl = document.querySelector<HTMLElement>(host);
    if (!hostEl) return;
    const els = hostEl.matches(sel) ? [hostEl] : Array.from(hostEl.querySelectorAll<HTMLElement>(sel));
    if (!els.length) return;
    const amounts = Array.isArray(amount) ? amount : null;
    els.forEach((el, i) => {
      const a = (amounts ? amounts[Math.min(i, amounts.length - 1)] : (amount as number)) * k;
      el.setAttribute("data-px", "");
      gsap.fromTo(
        el,
        { "--py": `${a}px` } as any,
        {
          "--py": `${mode === "settle" ? 0 : -a}px`,
          ease: "none",
          scrollTrigger: { trigger: hostEl, start, end, scrub: lenis ? 0.5 : 0.8 },
        } as any
      );
    });
  }

  /** Positive = foreground (rises faster than the page); negative = background (lags). */
  const pass = (host: string, sel: string, amount: number | number[]) => layer(host, sel, amount, "pass", "top bottom", "bottom top");
  const settle = (host: string, sel: string, amount: number | number[], end: string) => layer(host, sel, amount, "settle", "top bottom", end);

  /** Sheets: the section underneath slows and dims while the next one slides over it. */
  function cover(targets: string[], host: string, factor: number) {
    const hostEl = document.querySelector<HTMLElement>(host);
    if (!hostEl) return;
    targets.forEach((sel) => {
      const el = document.querySelector<HTMLElement>(sel);
      if (!el) return;
      el.setAttribute("data-lag", "");
      gsap.fromTo(
        el,
        { "--lag": "0px", "--dim": 0 } as any,
        {
          "--lag": () => `${Math.round(window.innerHeight * factor)}px`,
          "--dim": 0.72,
          ease: "none",
          immediateRender: true,
          scrollTrigger: {
            trigger: hostEl,
            start: "top bottom",
            end: "top top",
            scrub: lenis ? 0.4 : 0.8,
            invalidateOnRefresh: true,
          },
        } as any
      );
    });
  }

  return { pass, settle, cover };
}
